import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import QuestionRenderer from '@/components/QuestionRenderer';
import ContentProtection from '@/components/ContentProtection';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Trophy,
  Target,
  Clock,
  CheckCircle,
  XCircle,
  Home,
  RotateCcw,
  ChevronDown,
  ChevronUp,
  Loader2
} from 'lucide-react';

const Results = () => {
  const navigate = useNavigate();
  const { attemptId } = useParams();
  const { getAuthHeaders, user } = useAuth();
  const isAdmin = user?.role === 'admin';
  
  const [loading, setLoading] = useState(true);
  const [results, setResults] = useState(null);
  const [expandedQuestions, setExpandedQuestions] = useState(new Set());

  useEffect(() => {
    const fetchResults = async () => {
      try {
        const response = await fetch(`${API}/exams/${attemptId}/review`, {
          credentials: 'include',
          headers: getAuthHeaders()
        });

        if (!response.ok) {
          throw new Error('Failed to fetch results');
        }

        const data = await response.json();
        setResults(data);
      } catch (error) {
        toast.error('Failed to load results');
        navigate('/dashboard');
      } finally {
        setLoading(false);
      }
    };

    fetchResults();
  }, [attemptId, getAuthHeaders, navigate]);

  const toggleQuestion = (questionId) => {
    setExpandedQuestions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(questionId)) {
        newSet.delete(questionId);
      } else {
        newSet.add(questionId);
      }
      return newSet;
    });
  };

  const formatTime = (seconds) => {
    if (!seconds) return 'N/A';
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}m ${secs}s`;
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-500';
    if (score >= 60) return 'text-yellow-500';
    return 'text-red-500';
  };

  const getScoreGradient = (score) => {
    if (score >= 80) return 'from-green-500 to-emerald-600';
    if (score >= 60) return 'from-yellow-500 to-orange-500';
    return 'from-red-500 to-rose-600';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  if (!results) {
    return null;
  }

  const { attempt, questions } = results;
  const score = attempt.score || 0;
  const passed = score >= 70;

  // Calculate category breakdown
  const categoryStats = {};
  questions.forEach(q => {
    if (!categoryStats[q.category]) {
      categoryStats[q.category] = { correct: 0, total: 0 };
    }
    categoryStats[q.category].total++;
    if (q.is_correct) {
      categoryStats[q.category].correct++;
    }
  });

  return (
    <ContentProtection isAdmin={isAdmin} userEmail={user?.email} userName={user?.name}>
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/90 dark:bg-slate-900/90 backdrop-blur-xl border-b border-slate-200 dark:border-slate-800">
        <div className="max-w-5xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Button variant="ghost" onClick={() => navigate('/dashboard')} data-testid="back-to-dashboard">
              <Home className="w-4 h-4 mr-2" />
              Dashboard
            </Button>
            <h1 className="font-semibold">Results</h1>
            <Button variant="outline" onClick={() => navigate('/training')} data-testid="try-again-btn">
              <RotateCcw className="w-4 h-4 mr-2" />
              Try Again
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-8">
        {/* Score Hero */}
        <Card className={`mb-8 overflow-hidden bg-gradient-to-br ${getScoreGradient(score)} text-white`}>
          <CardContent className="p-8">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="text-center md:text-left">
                <p className="text-white/80 mb-2">{attempt.exam_type} {attempt.mode === 'training' ? 'Training' : 'Exam'}</p>
                <h2 className="text-4xl md:text-5xl font-bold mb-2">
                  {passed ? 'Congratulations!' : 'Keep Practicing!'}
                </h2>
                <p className="text-white/80">
                  {passed ? 'You passed this practice session.' : 'Review the explanations to improve.'}
                </p>
              </div>
              
              <div className="relative">
                <div 
                  className="w-36 h-36 rounded-full flex items-center justify-center bg-white/20 backdrop-blur-sm"
                  style={{
                    background: `conic-gradient(white ${score}%, rgba(255,255,255,0.2) ${score}%)`
                  }}
                >
                  <div className="w-28 h-28 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
                    <div className="text-center">
                      <div className="text-4xl font-bold">{score.toFixed(0)}%</div>
                      <div className="text-sm text-white/80">Score</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="bg-white dark:bg-slate-900">
            <CardContent className="p-4 text-center">
              <Target className="w-6 h-6 mx-auto mb-2 text-blue-500" />
              <p className="text-2xl font-bold">{attempt.correct_count + attempt.incorrect_count}</p>
              <p className="text-sm text-slate-600 dark:text-slate-400">Total Questions</p>
            </CardContent>
          </Card>
          
          <Card className="bg-white dark:bg-slate-900">
            <CardContent className="p-4 text-center">
              <CheckCircle className="w-6 h-6 mx-auto mb-2 text-green-500" />
              <p className="text-2xl font-bold text-green-600">{attempt.correct_count}</p>
              <p className="text-sm text-slate-600 dark:text-slate-400">Correct</p>
            </CardContent>
          </Card>
          
          <Card className="bg-white dark:bg-slate-900">
            <CardContent className="p-4 text-center">
              <XCircle className="w-6 h-6 mx-auto mb-2 text-red-500" />
              <p className="text-2xl font-bold text-red-600">{attempt.incorrect_count}</p>
              <p className="text-sm text-slate-600 dark:text-slate-400">Incorrect</p>
            </CardContent>
          </Card>
          
          <Card className="bg-white dark:bg-slate-900">
            <CardContent className="p-4 text-center">
              <Clock className="w-6 h-6 mx-auto mb-2 text-purple-500" />
              <p className="text-2xl font-bold">{formatTime(attempt.time_used)}</p>
              <p className="text-sm text-slate-600 dark:text-slate-400">Time Used</p>
            </CardContent>
          </Card>
        </div>

        {/* Tabs for Category Breakdown and Question Review */}
        <Tabs defaultValue="breakdown" className="space-y-6">
          <TabsList className="w-full justify-start bg-white dark:bg-slate-900 p-1 rounded-xl">
            <TabsTrigger value="breakdown" className="rounded-lg" data-testid="tab-breakdown">
              Category Breakdown
            </TabsTrigger>
            <TabsTrigger value="review" className="rounded-lg" data-testid="tab-review">
              Question Review
            </TabsTrigger>
          </TabsList>

          <TabsContent value="breakdown">
            <Card className="bg-white dark:bg-slate-900">
              <CardHeader>
                <CardTitle>Performance by Category</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {Object.entries(categoryStats).map(([category, stats]) => {
                  const percentage = (stats.correct / stats.total) * 100;
                  return (
                    <div key={category}>
                      <div className="flex justify-between text-sm mb-2">
                        <span className="font-medium">{category}</span>
                        <span className={percentage >= 70 ? 'text-green-600' : 'text-red-600'}>
                          {stats.correct}/{stats.total} ({percentage.toFixed(0)}%)
                        </span>
                      </div>
                      <Progress value={percentage} className="h-3" />
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="review">
            <Card className="bg-white dark:bg-slate-900">
              <CardHeader>
                <CardTitle>Question Review</CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[600px] pr-4">
                  <div className="space-y-6">
                    {questions.map((q, idx) => (
                      <div
                        key={q.question_id}
                        className={`border-2 rounded-xl overflow-hidden ${
                          q.is_correct ? 'border-green-200 dark:border-green-900' : 'border-red-200 dark:border-red-900'
                        }`}
                      >
                        <button
                          className="w-full p-4 flex items-center justify-between text-left hover:bg-slate-50 dark:hover:bg-slate-800/50"
                          onClick={() => toggleQuestion(q.question_id)}
                          data-testid={`question-${idx + 1}`}
                        >
                          <div className="flex items-center gap-3">
                            {q.is_correct ? (
                              <CheckCircle className="w-5 h-5 text-green-500 shrink-0" />
                            ) : (
                              <XCircle className="w-5 h-5 text-red-500 shrink-0" />
                            )}
                            <span className="font-medium">Question {idx + 1}</span>
                            <span className={`text-xs px-2 py-0.5 rounded-full ${
                              q.category === 'PMP' ? 'bg-blue-100 text-blue-700' :
                              q.category === 'CAPM' ? 'bg-purple-100 text-purple-700' :
                              'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300'
                            }`}>
                              {q.category}
                            </span>
                          </div>
                          {expandedQuestions.has(q.question_id) ? (
                            <ChevronUp className="w-5 h-5 text-slate-400" />
                          ) : (
                            <ChevronDown className="w-5 h-5 text-slate-400" />
                          )}
                        </button>
                        
                        {expandedQuestions.has(q.question_id) && (
                          <div className="p-5 pt-0 border-t border-slate-200 dark:border-slate-700">
                            <QuestionRenderer
                              question={q}
                              questionIndex={idx}
                              userAnswer={q.user_answer}
                              showExplanation={true}
                              isReview={true}
                            />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
    </ContentProtection>
  );
};

export default Results;
